import { format, parse, subDays } from 'date-fns';

import { UserData } from '@/types/UserData';

import { STATE_ZIP_CODE_RANGES } from './constants';
import { StepTwoFormData } from './schema';

/**
 * Formats a date as MM/DD/YYYY
 * example:
 * const date = '2020-06-25T11:00:00Z';
 * getFormattedDate(date, DATE_MONTH_DAY_YEAR_TWO_DIGIT) // 06/25/2020
 */
export const DATE_MONTH_DAY_YEAR_TWO_DIGIT = 'MM/dd/yyyy';

/**
 * Formats a date as YYYY-MM-DD
 * example:
 * const date = '2020-06-25T11:00:00Z';
 * getFormattedDate(date, DATE_YEAR_MONTH_DAY_TWO_DIGIT) // 2020-06-25
 */
export const DATE_YEAR_MONTH_DAY_TWO_DIGIT = 'yyyy-MM-dd';

export function isUnder18YearsOld(dateString: string): boolean | null {
  if (!dateString) {
    return null;
  }

  const birthDate = new Date(dateString);
  const today = new Date();

  // Create a date that is 18 years before today
  const date18YearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());

  return birthDate >= date18YearsAgo;
}

export function isEmptyData(obj: object): boolean {
  const keyArray = Object.keys(obj);
  for (let i = 0; i < keyArray.length; i++) {
    const key = keyArray[i];
    if (obj[key as keyof typeof obj]) {
      return false;
    }
  }
  return true;
}

export function formatPhoneNumber(phone: string): string {
  if (!phone) {
    return '';
  }

  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length !== 10) {
    console.error('Phone number must be exactly 10 digits');
    return '';
  }

  const areaCode = cleaned.slice(0, 3);
  const centralOffice = cleaned.slice(3, 6);
  const lineNumber = cleaned.slice(6, 10);

  return `(${areaCode})${centralOffice}-${lineNumber}`;
}

export const getValueOrDefault = (
  stepTwoData: StepTwoFormData,
  user?: UserData
): StepTwoFormData => {
  if (!user || !isEmptyData(stepTwoData)) {
    return stepTwoData;
  }

  const address = user?.addresses?.length
    ? (user.addresses.find((el) => el.addressType === 'Primary') ?? user.addresses[0])
    : undefined;

  const dateOfBirth = user?.user?.dob
    ? parse(user?.user?.dob, DATE_MONTH_DAY_YEAR_TWO_DIGIT, new Date())
    : '';
  const dateOfBirthValue = format(dateOfBirth, DATE_YEAR_MONTH_DAY_TWO_DIGIT);

  const phone = user?.contacts?.find((el) => el.contactType === 'Cell')?.contactInfo;
  const formattedPhone = formatPhoneNumber(phone || '');
  const email = user?.contacts?.find((el) => el.contactType === 'Email')?.contactInfo;

  const result: StepTwoFormData = {
    firstName: user?.user?.firstName || '',
    lastName: user?.user?.lastName || '',
    dateOfBirth: dateOfBirthValue || '',
    gender: user.user.gender || '',
    email: email || '',
    phoneNumber: formattedPhone || '',
    address1: address?.addressLine1 || '',
    address2: address?.addressLine2 || '',
    city: address?.city || '',
    state: address?.state || '',
    zip: address?.zip || '',
  };
  return result;
};

export const getMaxCalendarDate = (): string => {
  const maxDobDate = subDays(new Date(), 1);
  // Get yesterday and use it for magex DOB to prevent future dates
  // Format as 'yyyy-MM-dd'
  return format(maxDobDate, DATE_YEAR_MONTH_DAY_TWO_DIGIT);
};

export const isZipValidForState = (state: string, zip: string): boolean => {
  const numZip = parseInt(zip, 10);
  return (STATE_ZIP_CODE_RANGES[state] || []).some(([min, max]) => numZip >= min && numZip <= max);
};
